import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { App, Input } from 'antd'
import type { SessionAccount } from '@super-mega/server/contracts/auth'
import { ORDER_SEARCH_BATCH_ASYNC_THRESHOLD, type ServiceOrder } from '@super-mega/server/contracts/order'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import { useBatchJobCompletion } from '@/hooks/use-batch-job-completion.ts'
import type { PageTableNavigate } from '@/hooks/use-page-table.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { useOrderBatchUpdateRefresh } from '../hooks/use-order-batch-update-refresh.ts'
import { type OrderListSearch } from '../order-list-search.ts'

type ServiceOrderRecord = ServiceOrder

export function useServiceListActions({
  search,
  navigateSearch,
  currentSession,
  rows,
  total
}: {
  search: OrderListSearch
  navigateSearch: PageTableNavigate<OrderListSearch>
  currentSession: SessionAccount
  rows: ServiceOrder[] | undefined
  total: number
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const router = useRouter()
  const remove = useMutation(trpc.order.service.remove.mutationOptions())

  const batchRemove = useMutation(trpc.order.service.batchRemove.mutationOptions())

  const batchBySearch = useMutation(trpc.order.service.batchBySearch.mutationOptions())

  const [searchBatchWarnings, setSearchBatchWarnings] = useState<string[]>([])

  const [searchBatchJobId, setSearchBatchJobId] = useState<string>()

  const searchBatchStatus = useQuery({
    ...trpc.order.service.batchBySearchStatus.queryOptions({ jobId: searchBatchJobId ?? '' }),
    enabled: Boolean(searchBatchJobId),
    refetchInterval: (query) =>
      ['completed', 'failed', 'missing'].includes(query.state.data?.state ?? '') ? false : 3000
  })

  const busy = remove.isPending || batchRemove.isPending || batchBySearch.isPending

  const [selection, setSelection] = useState<{ search: OrderListSearch; ids: number[] }>({ search, ids: [] })

  const pageIds = new Set(rows?.map((order) => order.id))

  const selectedIds = selection.search === search ? selection.ids.filter((id) => pageIds.has(id)) : []

  const refreshOrders = async () => {
    setSelection({ search, ids: [] })
    await Promise.all([
      refreshMoneyQueries(queryClient, trpc),
      queryClient.invalidateQueries(trpc.order.groups.list.queryFilter()),
      queryClient.invalidateQueries(trpc.order.groups.overview.queryFilter()),
      queryClient.invalidateQueries(trpc.file.list.queryFilter())
    ])
    const refreshed = queryClient.getQueryData(trpc.order.service.list.queryKey(search))
    if (search.current > 1 && refreshed && refreshed.data.length === 0) {
      const current = Math.max(1, Math.ceil(refreshed.total / search.pageSize))
      await navigateSearch({ search: (previous) => ({ ...previous, current }) })
    }
  }

  const refreshBatchUpdate = useOrderBatchUpdateRefresh(refreshOrders)

  useBatchJobCompletion({
    jobId: searchBatchJobId,
    state: searchBatchStatus.data?.state,
    onComplete: refreshBatchUpdate,
    onError: () => {
      void message.warning('列表刷新失败，请手动刷新查看结果')
    }
  })

  const removeOrder = async (id: number) => {
    try {
      await remove.mutateAsync({ serviceOrderId: id })
      void message.success('服务订单已删除')
      await refreshOrders()
    } catch (error) {
      void message.error(getErrorMessage(error, '服务订单删除失败'))
    }
  }

  const confirmBatchRemove = () => {
    const ids = [...selectedIds]
    modal.confirm({
      title: `删除已选 ${ids.length} 个服务订单？`,
      content: currentSession.isSuperAdmin
        ? '系统会逐条尝试删除订单及关联账单、明细，已有资金分配或关联冲突的订单计为失败，其余继续删除。不会删除收款、退款或收据，删除后不可恢复。'
        : '系统会逐条尝试删除，仅可删除待办理或已归档且没有账单的订单。不符合条件的订单计为失败，其余继续删除，删除后不可恢复。',
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await batchRemove.mutateAsync({ ids })
          const text = `删除完成：成功 ${result.successCount} 个，失败 ${result.failedCount} 个`
          if (result.failedCount === 0) {
            void message.success(text)
          } else if (result.successCount === 0) {
            void message.error(text)
          } else {
            void message.warning(text)
          }
          await refreshOrders()
        } catch (error) {
          void message.error(getErrorMessage(error, '批量删除服务订单失败'))
          throw error
        }
      }
    })
  }

  const confirmSearchBatch = (action: 'delete' | 'forceDelete') => {
    const count = total
    const expected = `强制删除 ${count} 个订单`
    let confirmation = ''
    const execute = async () => {
      try {
        const result = await batchBySearch.mutateAsync({
          action,
          search,
          expectedCount: count,
          confirmation: action === 'forceDelete' ? confirmation : undefined
        })
        setSearchBatchWarnings(result.warnings)
        if (result.queued) {
          setSearchBatchJobId(result.jobId)
          void message.success(`已提交 ${result.count} 个订单到异步队列`)
        } else {
          const summary = `处理完成：成功 ${result.successCount} 个，失败 ${result.failedCount} 个`
          if (result.failedCount || result.warnings.length) {
            void message.warning(summary)
          } else {
            void message.success(summary)
          }
        }
        await refreshOrders()
      } catch (error) {
        void message.error(getErrorMessage(error, '按搜索批量操作失败'))
        throw error
      }
    }
    modal.confirm({
      title: `${action === 'forceDelete' ? '强制删除' : '删除'}当前搜索结果中的 ${count} 个服务订单？`,
      content:
        action === 'forceDelete' ? (
          <div>
            <p>将永久删除匹配的订单及关联数据。</p>
            <p>请输入「{expected}」确认。</p>
            <Input
              aria-label="确认按搜索强制删除"
              onChange={(event) => {
                confirmation = event.target.value
              }}
            />
          </div>
        ) : (
          `将按当前已提交的搜索条件处理全部匹配订单，逐条执行并统计失败；超过 ${ORDER_SEARCH_BATCH_ASYNC_THRESHOLD} 条会进入异步队列。删除后不可恢复。`
        ),
      okText: '确认执行',
      okButtonProps: { danger: true },
      onOk: () => {
        if (action === 'forceDelete' && confirmation.trim() !== expected) {
          void message.error(`请输入「${expected}」`)
          throw new Error('确认文字不匹配')
        }
        if (action === 'forceDelete') {
          modal.confirm({
            title: `第二次确认：永久删除 ${count} 个服务订单？`,
            content: '订单及关联数据将永久删除，无法恢复。',
            okText: '永久删除',
            okButtonProps: { danger: true },
            onOk: execute
          })
          return
        }
        return execute()
      }
    })
  }

  const confirmRemove = (order: ServiceOrderRecord) => {
    modal.confirm({
      title: `确认删除订单 ${order.orderNo}？`,
      content: currentSession.isSuperAdmin
        ? '将删除订单及其关联账单、账单明细，删除后不可恢复。已有收款或退款分配的账单需先处理资金记录；不会删除收款、退款或收据。'
        : '仅可删除待办理或已归档且没有账单的订单，删除后不可恢复。',
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: () => removeOrder(order.id)
    })
  }

  const openContractsBySearch = async () => {
    const contractTab = window.open('', '_blank')
    if (!contractTab) {
      void message.error('请允许浏览器打开新窗口')
      return
    }
    contractTab.opener = null
    try {
      const result = await queryClient.fetchQuery(trpc.order.service.contractBySearch.queryOptions(search))
      if (!contractTab.closed) {
        contractTab.location.replace(
          router.buildLocation({ to: '/orders/service/contracts/batch', search: { ids: result.ids } }).href
        )
      }
    } catch (error) {
      contractTab.close()
      void message.error(getErrorMessage(error, '无法批量打开合同'))
    }
  }
  return {
    selectedIds,
    setSelection,
    batchRemove,
    batchBySearch,
    busy,
    searchBatchWarnings,
    searchBatchJobId,
    setSearchBatchJobId,
    searchBatchStatus,
    refreshBatchUpdate,
    confirmBatchRemove,
    confirmSearchBatch,
    confirmRemove,
    refreshOrders,
    openContractsBySearch
  }
}
